import * as React from 'react';

import Skeleton from 'react-loading-skeleton';
import tinytime from 'tinytime';

import { Button, Text } from '@components/common';
import { WindowHeader, WindowLayout, WindowLayoutBody } from '@components/layouts/WindowLayout';
import { PlaceholderPlayer } from '@components/RepoSessionApp/RecordingWidget';
import { CalendarSVG, PersonSVG, PublicAssetLogoSVG } from '@components/svgs';
import VideoPlayer from '@components/VideoPlayer';
import { track } from 'components/tracking';

const template = tinytime('{Do} {MMMM} {YYYY}');

type Props = {
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  date?: Date;
  name?: string;
  playbackUrls?: MuxPlaybackUrls;
  clip?: PublicClip;
  renderSpeakerSection?: () => React.ReactElement;
  signUpUrl?: string;
  hasLiveStream?: boolean;
  sessionAt?: string;
};
export const VideoPage: React.FC<React.PropsWithChildren<Props>> = ({
  isLoading,
  title,
  subtitle,
  date,
  name,
  playbackUrls,
  clip,
  renderSpeakerSection,
  signUpUrl,
  hasLiveStream,
  sessionAt
}) => {
  let sessionAtInThePast = false;

  if (sessionAt) {
    sessionAtInThePast = sessionAt.length > 0 && new Date(sessionAt) < new Date();
  }

  return (
    <WindowLayout>
      <WindowHeader>
        <a href='/'>
          <PublicAssetLogoSVG />
        </a>
        <div className='ml-auto flex'>
          <Button link href='/users/sign_in'>
            Log in
          </Button>
          <Button primary href={signUpUrl}>
            Sign up for free
          </Button>
        </div>
      </WindowHeader>
      <WindowLayoutBody className='h-full bg-white'>
        <div className='px-page py-6'>
          <section>
            {isLoading && !title && <Skeleton className='mb-6 h-8 max-w-sm' />}
            <div className='mb-3 space-y-2'>
              {title && <Text h='700'>{title}</Text>}
              {subtitle && <Text mb='4'>{subtitle}</Text>}
            </div>
            {!isLoading && (
              <div className='mb-6 flex space-x-4'>
                {date && (
                  <div className='flex items-center space-x-2'>
                    <CalendarSVG />
                    <Text>{template.render(date)}</Text>
                  </div>
                )}
                {name && (
                  <div className='flex items-center space-x-2'>
                    <PersonSVG />
                    <Text>{name}</Text>
                  </div>
                )}
              </div>
            )}
          </section>
          <div className='mx-auto max-w-3xl'>
            {!sessionAtInThePast && hasLiveStream && (
              <div className='flex w-full items-center justify-center bg-gray-50'>
                <section className='flex h-full flex-col'>
                  <div className='py-6 text-center'>
                    <div className='font-bold text-gray-700'>This interview hasn't started yet.</div>
                    <div className='text-sm text-gray-500'>
                      You can watch the live stream here after refreshing the page.
                    </div>
                  </div>
                </section>
              </div>
            )}

            {sessionAtInThePast && !playbackUrls && <PlaceholderPlayer state='errored' />}

            {playbackUrls && (
              <VideoPlayer
                liveStream={hasLiveStream}
                className='w-full'
                src={playbackUrls.stream_url}
                poster={playbackUrls.thumbnail_url}
                onPlay={() =>
                  track('played_video', {
                    page: 'public_repo',
                    artifact_type: clip ? 'highlight' : 'session_recording'
                  })
                }
                storyboardUrl={playbackUrls.storyboard_url}
              />
            )}

            {renderSpeakerSection?.()}
          </div>
        </div>
      </WindowLayoutBody>
    </WindowLayout>
  );
};
